/**
 * Widget "Demander un rendez-vous" — M. Auto Sud Loire
 * Vanilla JS, zéro dépendance. Flux simplifié (jamais de prix) : Prestation → Date souhaitée →
 * Créneau → Coordonnées → Véhicule (+ soumission). Réutilise le framework CSS de devis-widget.css
 * (.mauto-devis, .mauto-devis-mobile-topbar, .mauto-devis-actions, etc.) pour hériter du même
 * comportement responsive (plein écran mobile, bouton sticky, flèche retour) sans rien
 * dupliquer côté CSS — seul le calendrier a son propre style (cf. devis-rdv-widget.css).
 *
 * Monté à la demande par devis-floating-widget.js (bouton flottant dédié) via
 * window.MautoRdvWidget.initWidget(container) — jamais automatiquement au chargement de page.
 *
 * Deuxième point d'entrée : `initWidget(container, options)` avec `options.prestations` (tableau
 * de libellés, + `options.vehicule`) pré-rempli — utilisé par le bouton "Prendre rendez-vous" de
 * l'étape Estimation du widget de devis (devis-widget.js, handoffToRdv) via
 * devis-floating-widget.js, qui écoute l'événement DOM `mauto:open-rdv-widget` et route vers cet
 * overlay. Dans ce cas l'étape 1 (Prestation) est sautée : le parcours démarre directement à
 * l'étape 2 (Date), la ou les prestations et le véhicule étant déjà connus.
 *
 * `options.prestations` peut contenir 1 seul libellé (handoff mono-prestation classique — état
 * interne `state.prestation`, payload et endpoint /api/rdv strictement identiques à avant cette
 * fonctionnalité panier) ou plusieurs (handoff panier ≥2 prestations depuis devis-widget.js — état
 * interne `state.cartPrestations`, payload `prestations` et endpoint dédié /api/rdv-groupe, cf.
 * onSubmit). Rien n'est recréé pour le reste du parcours : mêmes fonctions renderStep*, mêmes
 * validations, que ce soit un item unique ou un panier.
 *
 * Troisième point d'entrée : handoff direct depuis une fiche produit pneu (product.pneu.json,
 * bloc "retrait") via localStorage (cf. consumePendingPneuRdvItem) plutôt que `options` — la
 * fiche produit ne connaît pas ce widget, elle écrit juste l'item puis ouvre le bouton flottant
 * RDV normalement. `state.pneuPrefilled` saute à la fois le choix de prestation ET le tiroir
 * dimension (6 étapes) : le pneu exact est déjà connu (metafields Shopify), direction Date.
 * Endpoint/payload /api/rdv inchangés (state.prestation = RDV_PNEU_PRESTATION), seul state.pneu
 * porte en plus `marque`/`modele` (facultatifs, ignorés par la validation serveur existante).
 */
(function () {
  'use strict';

  // Doit rester alignée avec sync-server/server.js (RDV_PRESTATIONS) et avec les libellés de
  // DEFAULT_PRICING (même fichier) — copier-coller le texte exact, ne pas retaper.
  var RDV_PRESTATIONS = [
    'Vidange (forfait huile et filtre à huile)',
    'Entretien / Révision (vidange + filtres complémentaires)',
    'Pack Contrôle technique - Contre visite remboursés***',
    'Plaquettes de frein avant (remplacement seul)',
    'Plaquettes de frein arrière (remplacement seul)',
    'Disques + plaquettes de frein avant',
    'Disques + plaquettes de frein arrière',
    'Distribution (kit + pompe à eau)',
    'Embrayage (kit complet, hors volant moteur)',
    'Amortisseurs avant (paire)',
    'Amortisseurs arrière (paire)',
    'Géométrie / parallélisme',
    'Recharge clim',
    'Entretien clim anti-allergènes',
    'Batterie (remplacement)',
    'Pneus (achat et pose)',
    'Diagnostic / recherche de panne',
    'Décalaminage à l\'hydrogène',
  ];
  var AUTRE_LABEL = 'Autre';

  // ── Vignettes de raccourci ────────────────────────────────────────────────────────────────
  // Les mêmes 6 que le parcours devis, dans le même ordre, avec les mêmes icônes : le client doit
  // voir la même chose quel que soit le bouton sur lequel il a cliqué. La liste des clés est posée
  // par le HTML (data-quick-picks), comme pour l'autre widget, pour ne la maintenir qu'à un
  // endroit par surface.
  //
  // Ce widget ne raisonne pas en clés tarifaires mais en LIBELLÉS de RDV_PRESTATIONS ci-dessus.
  // D'où cette table de correspondance. Les libellés doivent être recopiés au caractère près de
  // RDV_PRESTATIONS : une différence et le <select> de l'étape 1 n'affiche plus la valeur.
  // Pour les familles (freinage, amortisseurs) on retient la variante AVANT, exactement comme le
  // parcours devis qui pré-sélectionne la première variante de la famille — le client peut
  // toujours changer ensuite dans le menu déroulant.
  var QUICK_PRESTATIONS = {
    vidange: 'Vidange (forfait huile et filtre à huile)',
    amortisseurs: 'Amortisseurs avant (paire)',
    freinage: 'Plaquettes de frein avant (remplacement seul)',
    pneus: 'Pneus (achat et pose)',
    distribution: 'Distribution (kit + pompe à eau)',
  };
  // Vignettes sans prestation dédiée : passent par « Autre (précisez) » avec la description déjà
  // remplie. Le pare-brise est dans ce cas, il n'existe dans aucune des deux listes (ni tarifs
  // côté devis, ni RDV_PRESTATIONS ici). Même table, mêmes textes que QUICK_AUTRE dans
  // devis-widget.js — les deux parcours doivent produire la même demande.
  var QUICK_AUTRE = {
    pare_brise: 'Pare-brise (remplacement ou réparation d\'impact)',
  };
  // Libellés courts des vignettes : les libellés officiels sont beaucoup trop longs pour tenir
  // sous une icône. Doit rester aligné avec QUICK_LABELS dans devis-widget.js.
  var QUICK_LABELS = {
    vidange: 'Vidange', amortisseurs: 'Suspension', freinage: 'Freinage',
    pneus: 'Pneus', distribution: 'Distribution', pare_brise: 'Pare-brise',
    revision: 'Entretien', controle_technique: 'Pack CT', geometrie: 'Géométrie',
    climatisation: 'Climatisation', decalaminage: 'Décalaminage', diagnostic: 'Diagnostic',
  };

  // Icônes au trait, communes aux deux widgets (assets/mauto-icones.js). Chaîne vide si le fichier
  // partagé n'a pas pu se charger : les libellés restent lisibles seuls.
  function icone(cle) {
    var m = window.MautoIcones;
    return m ? m.pour(cle) : '';
  }
  function iconFor(key) {
    var m = window.MautoIcones;
    if (!m) return '';
    return m.pour(key === 'revision' ? 'vidange' : key, 'autre');
  }

  // Règles de disponibilité (créneaux, jours fériés, week-ends, fermetures, délai minimum) :
  // elles ne vivent plus ici mais dans assets/mauto-calendrier.js, partagé avec la page panier.
  // Ce fichier doit donc être chargé AVANT celui-ci (cf. snippets/devis-floating-widget.liquid).
  // sync-server/server.js applique les mêmes règles et fait foi à la soumission.
  var CAL = window.MautoCalendrier;
  var CRENEAUX = CAL ? CAL.CRENEAUX : [];
  var RDV_MIN_DELAY_H = CAL ? CAL.RDV_DELAI_HEURES : 48;

  // Dimension pneu obligatoire — déclenché uniquement pour cette prestation exacte (doit
  // rester aligné avec RDV_PNEU_PRESTATION côté sync-server/server.js). Listes de valeurs
  // "les plus courantes" affichées par défaut ; NE_SAIT_PAS est la valeur sentinelle acceptée
  // pour les indices de charge/vitesse (l'étape reste obligatoire à remplir, mais le client
  // peut explicitement indiquer qu'il ne connaît pas la valeur plutôt que d'être bloqué).
  var RDV_PNEU_PRESTATION = 'Pneus (achat et pose)';
  var NE_SAIT_PAS = 'ne_sait_pas';
  // Les listes largeur/hauteur/diamètre/charge/vitesse ne sont plus écrites ici, et ne sont plus
  // dupliquées avec devis-widget.js : les deux tiroirs les tirent désormais du catalogue lui-même,
  // via assets/mauto-pneu-dimensions.js. Motif du changement (11/09/2026) : les constantes figées
  // — PNEU_CHARGES = ['89','93'], PNEU_VITESSES = ['H','V','W','Y','Z'] — rendaient inatteignables
  // 82 des 89 fiches publiées, dont les trois 175/65 R14 en 86T et 82H. Le risque que signalait
  // l'ancien commentaire (une valeur présente d'un côté et pas de l'autre) disparaît de lui-même,
  // puisqu'il n'y a plus qu'une seule source.
  var PNEU_SAISONS = [
    { key: 'ete', label: 'Été' },
    { key: '4saisons', label: '4 saisons' },
    { key: 'hiver', label: 'Hiver' },
    { key: 'toutes', label: 'Toutes' },
  ];

  // Index des dimensions réellement publiées, chargé une fois depuis /collections/pneus?view=
  // pneus-index (cf. assets/mauto-pneu-dimensions.js et templates/collection.pneus-index.liquid).
  // Même mécanique que dans devis-widget.js — volontairement, pour que les deux tiroirs se
  // comportent à l'identique. Fonctionne aussi index non chargé (repli sur l'échelle normalisée),
  // le client n'est jamais bloqué devant une grille vide.
  var pneuIndex = null;
  function chargerPneuIndex() {
    var mod = window.MautoPneuDimensions;
    if (!mod) return Promise.resolve(null);
    if (pneuIndex) return Promise.resolve(pneuIndex);
    return mod.charger().then(function (idx) { pneuIndex = idx; return idx; });
  }
  function pneuOptions(champ, sel) {
    var mod = window.MautoPneuDimensions;
    if (!mod) return { dispo: [], toutes: [] };
    return mod.options(pneuIndex, champ, sel);
  }

  // Saisons proposées dans le tiroir : celles réellement publiées au catalogue, plus « Toutes »
  // qui n'est pas une saison mais l'absence de préférence. Même motif et même comportement que
  // dans devis-widget.js — le catalogue ne contient que de l'Été au 11/09/2026, et proposer une
  // saison qu'on ne vend pas donne un choix qui ne mène nulle part. Si l'index n'a pas pu être
  // chargé, on affiche tout plutôt que d'amputer le parcours sur un incident réseau.
  var SAISON_EXPLICATIONS = {
    ete: 'Été&nbsp;: meilleure tenue de route par temps sec/chaud.',
    hiver: 'Hiver&nbsp;: adhérence en dessous de 7°C, neige/verglas.',
    '4saisons': '4 saisons&nbsp;: compromis toute l\'année sans changer de jeu.',
  };
  function saisonsProposees() {
    var dispo = {};
    if (pneuIndex && pneuIndex.fiches) {
      pneuIndex.fiches.forEach(function (f) { if (f.saison) dispo[f.saison] = 1; });
    }
    var indexVide = !Object.keys(dispo).length;
    return PNEU_SAISONS.filter(function (s) {
      return indexVide || s.key === 'toutes' || dispo[s.label];
    });
  }

  // Handoff direct depuis une fiche produit pneu (product.pneu.json, bloc "retrait") : le client
  // a déjà choisi un pneu précis (marque/modèle/dimension connus via les metafields Shopify), donc
  // ni le choix de prestation ni le tiroir dimension (6 étapes) n'ont de raison d'être — seul le
  // reste du parcours RDV (Date → Créneau → Coordonnées → Véhicule) doit s'afficher. Même
  // mécanisme que le handoff panier vers devis-widget.js (localStorage, consommé une seule fois
  // au montage) plutôt qu'un appel direct, pour rester découplé de devis-floating-widget.js.
  var PNEU_RDV_HANDOFF_KEY = 'mautoPendingPneuRdvItem';
  function consumePendingPneuRdvItem() {
    var raw;
    try { raw = window.localStorage.getItem(PNEU_RDV_HANDOFF_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { window.localStorage.removeItem(PNEU_RDV_HANDOFF_KEY); } catch (e) { /* ignoré */ }
    try {
      var item = JSON.parse(raw);
      return (item && item.largeur) ? item : null;
    } catch (e) {
      return null;
    }
  }

  var IMMAT_REGEX = /^[A-Za-z]{2}[\s-]?\d{3}[\s-]?[A-Za-z]{2}$/;
  function normalizeImmat(v) {
    var compact = v.replace(/[\s-]/g, '').toUpperCase();
    if (compact.length === 7) return compact.slice(0, 2) + '-' + compact.slice(2, 5) + '-' + compact.slice(5, 7);
    return v.trim().toUpperCase();
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function el(html) {
    var t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  // Délégations vers assets/mauto-calendrier.js — mêmes noms et mêmes signatures qu'avant, pour
  // ne toucher à aucun appelant. Si le fichier partagé manquait, le calendrier refuse de
  // s'afficher plutôt que de proposer des dates fausses (cf. renderStepDate).
  function toDateStr(d) { return CAL.versChaine(d); }
  function isSelectableDate(d, minDate, fermetures) { return CAL.estSelectionnable(d, minDate, fermetures); }

  var MONTH_NAMES = CAL ? CAL.MOIS : [];
  var WEEKDAY_LABELS = CAL ? CAL.JOURS_COURTS : [];

  function getApiBase(container) {
    return container.getAttribute('data-api-base') || window.MAUTO_DEVIS_API_BASE || '';
  }

  function initWidget(container, options) {
    options = options || {};
    var apiBase = getApiBase(container);
    if (!apiBase) {
      container.innerHTML = '<p class="mauto-devis-error">Widget de rendez-vous non configuré (API manquante).</p>';
      return;
    }

    // Fermetures déclarées par le propriétaire — chargées une fois en tâche de fond, sans
    // bloquer l'ouverture du widget. Si l'utilisateur est déjà sur l'étape Date quand la réponse
    // arrive, le calendrier est simplement re-rendu pour refléter les dates désormais grisées.
    var fermetures = [];
    fetch(apiBase + '/api/fermetures')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        fermetures = (data && data.fermetures) || [];
        if (state.step === 2) renderStepDate();
      })
      .catch(function () { /* échec silencieux — le serveur revalide de toute façon à la soumission */ });

    var prefillVehicule = options.vehicule || {};
    // Normalise le handoff : `options.prestations` (tableau, nouveau) prime sur l'ancien
    // `options.prestation` (chaîne unique) si les deux sont fournis. 1 seul libellé → state.prestation
    // (parcours mono-prestation, comportement identique à avant) ; ≥2 → state.cartPrestations
    // (parcours panier, cf. onSubmit). Le choix direct via le menu déroulant (renderStepPrestation,
    // aucun handoff) alimente aussi state.prestation exactement comme aujourd'hui.
    var handoffPrestations = Array.isArray(options.prestations)
      ? options.prestations.filter(Boolean)
      : (options.prestation ? [options.prestation] : []);
    // Handoff direct pneu (cf. consumePendingPneuRdvItem ci-dessus) : prioritaire sur tout le
    // reste — un client qui arrive ainsi n'a jamais pu passer par le dropdown Prestation.
    var pneuRdvItem = consumePendingPneuRdvItem();
    var state = {
      prestation: pneuRdvItem ? RDV_PNEU_PRESTATION : (handoffPrestations.length === 1 ? handoffPrestations[0] : (options.prestation || '')),
      cartPrestations: handoffPrestations.length > 1 ? handoffPrestations : [],
      autreDescription: '',
      dateSouhaitee: '', // 'YYYY-MM-DD'
      creneauSouhaite: '', // '8h-10h' | '10h-12h' | '14h-16h' | '16h-18h'
      client: { nom: '', prenom: '', telephone: '', telephoneFixe: '', email: '', codePostal: '' },
      vehicule: {
        immatriculation: prefillVehicule.immatriculation || '',
        marque: prefillVehicule.marque || '',
        modele: prefillVehicule.modele || '',
      },
      pneu: pneuRdvItem || { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' },
      pneuPrefilled: !!pneuRdvItem,
      step: 1,
    };
    var currentBackHandler = null;

    // Liste des vignettes, posée par le HTML comme pour le widget de devis (cf.
    // snippets/devis-floating-widget.liquid). Doit rester identique aux deux endroits : le client
    // doit voir les mêmes raccourcis quel que soit le bouton sur lequel il a cliqué.
    var quickPicks = (container.getAttribute('data-quick-picks') || '')
      .split(',').map(function (x) { return x.trim(); }).filter(Boolean);

    container.innerHTML = '';
    container.classList.add('mauto-devis');
    // Toujours ouvert via le bouton flottant (jamais affiché inline) → plein écran mobile actif
    // en permanence, comme le widget de devis sur les pages sans barre de recherche.
    container.classList.add('mauto-devis-flow-active');

    var mobileTopbar = el(
      '<div class="mauto-devis-mobile-topbar">' +
        '<button type="button" class="mauto-devis-mobile-back is-invisible" aria-label="Retour">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
        '</button>' +
        '<div class="mauto-devis-mobile-progress">' +
          '<span class="mauto-devis-mobile-dot" data-n="1"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="2"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="3"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="4"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="5"></span>' +
        '</div>' +
      '</div>'
    );
    container.appendChild(mobileTopbar);
    var mobileBackBtn = mobileTopbar.querySelector('.mauto-devis-mobile-back');
    mobileBackBtn.addEventListener('click', function () { if (currentBackHandler) currentBackHandler(); });

    var stepsBar = el(
      '<div class="mauto-devis-stepsbar">' +
        '<span class="mauto-devis-stepdot" data-n="1">1. Prestation</span>' +
        '<span class="mauto-devis-stepdot" data-n="2">2. Date</span>' +
        '<span class="mauto-devis-stepdot" data-n="3">3. Créneau</span>' +
        '<span class="mauto-devis-stepdot" data-n="4">4. Coordonnées</span>' +
        '<span class="mauto-devis-stepdot" data-n="5">5. Véhicule</span>' +
      '</div>'
    );
    container.appendChild(stepsBar);

    // Récap panier persistant (lecture seule — le tri/retrait d'items se fait uniquement côté
    // devis-widget.js avant le handoff) : n'apparaît que pour un handoff panier (state.cartPrestations
    // non vide), jamais pour un RDV mono-prestation classique. Même classe CSS que la barre panier
    // du widget de devis (devis-widget.css) pour une cohérence visuelle sans dupliquer le style.
    var cartBar = el('<div class="mauto-devis-cart-bar" hidden></div>');
    if (state.cartPrestations.length) {
      cartBar.hidden = false;
      cartBar.innerHTML =
        '<span class="mauto-devis-cart-label">' + icone('panier') + ' ' + state.cartPrestations.length + ' prestations pour ce rendez-vous</span>' +
        '<span class="mauto-devis-cart-items">' +
          state.cartPrestations.map(function (p) { return '<span class="mauto-devis-cart-item">' + esc(p) + '</span>'; }).join('') +
        '</span>';
    } else if (state.pneuPrefilled) {
      cartBar.hidden = false;
      var pneuLabel = [state.pneu.marque, state.pneu.modele].filter(Boolean).join(' ');
      var pneuDim = state.pneu.largeur + '/' + state.pneu.hauteur + ' R' + state.pneu.diametre;
      cartBar.innerHTML =
        '<span class="mauto-devis-cart-label">' + icone('pneus') + ' ' + esc(pneuLabel || 'Pneu sélectionné') + '</span>' +
        '<span class="mauto-devis-cart-items"><span class="mauto-devis-cart-item">' + esc(pneuDim) + '</span></span>';
    }
    container.appendChild(cartBar);

    // Vignettes de raccourci, identiques au parcours devis. Masquées pendant le tiroir dimension
    // pneu uniquement (cf. startPneuFlow / cancelPneuFlow), visibles le reste du temps — même
    // comportement que dans devis-widget.js.
    var quickPicksBar = null;
    if (quickPicks.length) {
      quickPicksBar = el('<div class="mauto-devis-quickpicks"></div>');
      container.appendChild(quickPicksBar);
    }

    var body = el('<div class="mauto-devis-body"></div>');
    container.appendChild(body);

    function renderQuickPicksBar() {
      if (!quickPicksBar) return;
      quickPicksBar.innerHTML = '';
      quickPicks.forEach(function (k) {
        var prestation = QUICK_PRESTATIONS[k];
        var viaAutre = !!QUICK_AUTRE[k];
        // Une clé qui ne correspond ni à une prestation de rendez-vous ni à une entrée
        // QUICK_AUTRE est ignorée : mieux vaut une vignette en moins qu'une vignette qui ne
        // mène nulle part.
        if (!prestation && !viaAutre) return;
        var btn = el(
          '<button type="button" class="mauto-devis-quickpick" data-key="' + esc(k) + '">' +
            '<span class="mauto-devis-quickpick-icon">' + iconFor(k) + '</span>' +
            '<span class="mauto-devis-quickpick-label">' + esc(QUICK_LABELS[k] || k) + '</span>' +
          '</button>'
        );
        btn.addEventListener('click', function () {
          var all = quickPicksBar.querySelectorAll('.mauto-devis-quickpick');
          for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
          btn.classList.add('is-active');
          if (viaAutre) {
            state.prestation = AUTRE_LABEL;
            // On ne pré-remplit que si le champ est vide : sinon un retour en arrière effacerait
            // ce que le client vient de taper.
            if (!String(state.autreDescription || '').trim()) state.autreDescription = QUICK_AUTRE[k];
            renderStepAutre();
            return;
          }
          state.prestation = prestation;
          if (prestation === RDV_PNEU_PRESTATION) startPneuFlow();
          else goToStep(2);
        });
        quickPicksBar.appendChild(btn);
      });
    }

    function setStep(n, backHandler) {
      state.step = n;
      currentBackHandler = backHandler || null;
      mobileBackBtn.classList.toggle('is-invisible', !currentBackHandler);
      var dots = stepsBar.querySelectorAll('.mauto-devis-stepdot');
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', Number(dots[i].getAttribute('data-n')) === n);
        dots[i].classList.toggle('is-done', Number(dots[i].getAttribute('data-n')) < n);
      }
      var mdots = mobileTopbar.querySelectorAll('.mauto-devis-mobile-dot');
      for (var j = 0; j < mdots.length; j++) {
        mdots[j].classList.toggle('is-active', Number(mdots[j].getAttribute('data-n')) === n);
        mdots[j].classList.toggle('is-done', Number(mdots[j].getAttribute('data-n')) < n);
      }
    }

    function goToStep(n) {
      if (n === 1) renderStepPrestation();
      else if (n === 2) renderStepDate();
      else if (n === 3) renderStepCreneau();
      else if (n === 4) renderStepCoordonnees();
      else if (n === 5) renderStepVehicule();
    }

    // ─── Tiroir dimension pneu (6 étapes obligatoires, sauf la dernière) ─────────────────────
    // Déclenché uniquement quand la prestation choisie est RDV_PNEU_PRESTATION. Réutilise les
    // classes CSS existantes (.mauto-devis-field, .mauto-devis-actions, .mauto-devis-btn-*,
    // .mauto-devis-error) plutôt qu'un vrai tiroir latéral coulissant — seules quelques classes
    // .mauto-pneu-* sont nouvelles (barre de progression, grille d'options, bloc "Guide
    // d'achat"), définies dans devis-rdv-widget.css. La barre/dots de l'étape RDV extérieure
    // (mobileTopbar/stepsBar) est masquée pendant ce sous-parcours, puis restaurée à la sortie.
    function pneuSummaryText() {
      var p = state.pneu;
      if (!p.largeur) return '';
      var s = p.largeur;
      if (p.hauteur) s += '/' + p.hauteur;
      if (p.diametre) s += ' R' + p.diametre;
      if (p.indiceCharge) s += ' ' + (p.indiceCharge === NE_SAIT_PAS ? '?' : p.indiceCharge);
      if (p.indiceVitesse) s += (p.indiceVitesse === NE_SAIT_PAS ? '?' : p.indiceVitesse);
      return s;
    }

    function startPneuFlow() {
      mobileTopbar.style.display = 'none';
      stepsBar.style.display = 'none';
      if (quickPicksBar) quickPicksBar.style.display = 'none';
      // Les listes sont construites sur le catalogue : tant qu'il n'est pas chargé il n'y a rien de
      // juste à afficher. L'index fait ~3 Ko et le cache de session évite tout nouvel aller-retour,
      // l'écran d'attente n'est donc visible qu'à la toute première ouverture.
      if (pneuIndex) { renderPneuStepLargeur(); return; }
      renderPneuAttente();
      chargerPneuIndex().then(function () { renderPneuStepLargeur(); });
    }

    function endPneuFlow() {
      mobileTopbar.style.display = '';
      stepsBar.style.display = '';
      goToStep(2);
    }

    function cancelPneuFlow() {
      mobileTopbar.style.display = '';
      stepsBar.style.display = '';
      if (quickPicksBar) quickPicksBar.style.display = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      // Panier (handoff ≥2 prestations) : pas de dropdown mono-prestation à réafficher — direction
      // Date, pneu laissé vide (la validation serveur bloquera la soumission finale avec un
      // message clair si "Pneus" fait partie du panier et que les dimensions manquent encore).
      if (state.cartPrestations.length) { renderStepDate(); return; }
      state.prestation = '';
      renderStepPrestation();
    }

    function renderPneuChrome(n, title, backHandler) {
      var pct = Math.round((n / 6) * 100);
      var summary = pneuSummaryText();
      return (
        '<div class="mauto-pneu-header">' +
          (backHandler ? '<button type="button" class="mauto-pneu-back" data-action="pneu-back" aria-label="Retour"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button>' : '<span class="mauto-pneu-back-spacer"></span>') +
          '<span class="mauto-pneu-title">Trouver mes pneus</span>' +
          '<button type="button" class="mauto-pneu-close" data-action="pneu-close" aria-label="Fermer">✕</button>' +
        '</div>' +
        (summary ? '<div class="mauto-pneu-recap"><span>Dimension : ' + esc(summary) + '</span><button type="button" class="mauto-pneu-edit" data-action="pneu-edit" aria-label="Modifier la dimension">✎</button></div>' : '') +
        '<div class="mauto-pneu-progress-row"><span>' + esc(title) + '</span><span>' + n + '/6</span></div>' +
        '<div class="mauto-pneu-progressbar"><div style="width:' + pct + '%"></div></div>'
      );
    }

    function wirePneuChrome(backHandler) {
      // Bug iOS Safari (position:fixed + -webkit-overflow-scrolling:touch, cf. devis-widget.css) :
      // après un remplacement de body.innerHTML, la barre de progression du tiroir pneu peut
      // rester peinte "détachée" en bas de l'écran physique au lieu de sa vraie position dans le
      // flux — le translateZ(0) posé sur .mauto-devis-body ne suffit pas seul dans tous les cas
      // (captures utilisateur iPhone, 2026-08-23). Remettre le scroll en haut et forcer un reflow
      // synchrone à chaque étape corrige le repaint.
      if (window.innerWidth <= 768) {
        body.scrollTop = 0;
        void body.offsetHeight;
      }
      var backBtn = body.querySelector('[data-action="pneu-back"]');
      if (backBtn && backHandler) backBtn.addEventListener('click', backHandler);
      body.querySelector('[data-action="pneu-close"]').addEventListener('click', cancelPneuFlow);
      var editBtn = body.querySelector('[data-action="pneu-edit"]');
      if (editBtn) editBtn.addEventListener('click', renderPneuStepLargeur);
      var infoBtn = body.querySelector('[data-action="pneu-info"]');
      if (infoBtn) {
        infoBtn.addEventListener('click', function () {
          var t = body.querySelector('.mauto-pneu-info-text');
          t.hidden = !t.hidden;
        });
      }
    }

    function wirePneuOptions(field, onPick) {
      var btns = body.querySelectorAll('.mauto-pneu-option');
      for (var i = 0; i < btns.length; i++) {
        btns[i].addEventListener('click', function (ev) {
          state.pneu[field] = ev.currentTarget.getAttribute('data-value');
          onPick();
        });
      }
    }

    // ── Grille de choix construite à partir du catalogue ────────────────────────────────────
    // Voir le commentaire jumeau dans devis-widget.js : par défaut on n'affiche que les valeurs
    // réellement publiées pour ce que le client a déjà choisi, et l'échelle normalisée complète
    // reste accessible derrière un lien, parce que l'indice saisi est le BESOIN du véhicule et
    // non une préférence — un client qui a besoin de 91V doit pouvoir le dire même si nous ne
    // l'avons pas, plutôt que de se rabattre sur un 88H sous-dimensionné.
    function renderPneuGrille(champ, field, labelDispo, labelToutes, lienLabel) {
      var sel = {
        largeur: champ === 'largeur' ? '' : state.pneu.largeur,
        hauteur: (champ === 'largeur' || champ === 'hauteur') ? '' : state.pneu.hauteur,
        diametre: champ === 'diametre' ? '' : state.pneu.diametre,
      };
      var o = pneuOptions(champ, sel);
      var tout = !!state['_pneuTout_' + champ] || o.dispo.length === 0;
      var liste = tout ? o.toutes : o.dispo;
      var auCatalogue = {};
      o.dispo.forEach(function (v) { auCatalogue[v] = 1; });
      return '<p class="mauto-devis-field-label">' + esc(tout ? labelToutes : labelDispo) +
          (tout && o.dispo.length ? ' <span class="mauto-pneu-legende">● disponible chez nous</span>' : '') +
        '</p>' +
        '<div class="mauto-pneu-grid">' +
          liste.map(function (v) {
            var cls = 'mauto-pneu-option' +
              (state.pneu[field] === v ? ' is-selected' : '') +
              (tout && auCatalogue[v] ? ' is-dispo' : '');
            return '<button type="button" class="' + cls + '" data-value="' + esc(v) + '">' + esc(v) + '</button>';
          }).join('') +
        '</div>' +
        (tout ? '' : '<button type="button" class="mauto-pneu-link" data-action="pneu-tout">' + esc(lienLabel) + '</button>');
    }

    function wirePneuGrille(champ, field, nextFn, rerender) {
      var lien = body.querySelector('[data-action="pneu-tout"]');
      if (lien) {
        lien.addEventListener('click', function () {
          state['_pneuTout_' + champ] = true;
          rerender();
        });
      }
      wirePneuOptions(field, nextFn);
    }

    function renderPneuAttente() {
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<p class="mauto-devis-loading">Chargement des dimensions disponibles…</p>';
      wirePneuChrome(null);
    }

    function renderPneuIndisponible() {
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<p class="mauto-devis-error">Les dimensions n\'ont pas pu être chargées. Appelez-nous au ' +
        '<a href="tel:+33240268661">02 40 26 86 61</a>, nous trouvons votre pneu avec vous.</p>';
      wirePneuChrome(null);
    }

    function dimensionLisible() {
      return esc(state.pneu.largeur) + '/' + esc(state.pneu.hauteur) + ' R' + esc(state.pneu.diametre);
    }

    // Étape 1 — Largeur.
    function renderPneuStepLargeur() {
      if (!window.MautoPneuDimensions) { renderPneuIndisponible(); return; }
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>La largeur, la hauteur et le diamètre sont inscrits sur le flanc du pneu (ex&nbsp;: 205/55 R16) ou dans le carnet d\'entretien du véhicule.</p>' +
        renderPneuGrille('largeur', 'largeur', 'Largeurs disponibles chez nous', 'Toutes les largeurs', 'Voir toutes les largeurs');
      wirePneuChrome(null);
      wirePneuGrille('largeur', 'largeur', renderPneuStepHauteur, renderPneuStepLargeur);
    }

    // Étape 2 — Hauteur.
    function renderPneuStepHauteur() {
      body.innerHTML =
        renderPneuChrome(2, 'Hauteur', renderPneuStepLargeur) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>La hauteur (en % de la largeur) est le deuxième nombre inscrit sur le flanc du pneu (ex&nbsp;: 205/<strong>55</strong> R16).</p>' +
        renderPneuGrille('hauteur', 'hauteur',
          'Hauteurs disponibles en ' + esc(state.pneu.largeur), 'Toutes les hauteurs', 'Voir toutes les hauteurs');
      wirePneuChrome(renderPneuStepLargeur);
      wirePneuGrille('hauteur', 'hauteur', renderPneuStepDiametre, renderPneuStepHauteur);
    }

    // Étape 3 — Diamètre.
    function renderPneuStepDiametre() {
      body.innerHTML =
        renderPneuChrome(3, 'Diamètre', renderPneuStepHauteur) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Le diamètre (en pouces) est inscrit après le "R" sur le flanc du pneu (ex&nbsp;: 205/55 R<strong>16</strong>) — c\'est aussi le diamètre de la jante.</p>' +
        renderPneuGrille('diametre', 'diametre',
          'Diamètres disponibles en ' + esc(state.pneu.largeur) + '/' + esc(state.pneu.hauteur),
          'Tous les diamètres', 'Voir tous les diamètres');
      wirePneuChrome(renderPneuStepHauteur);
      wirePneuGrille('diametre', 'diametre', renderPneuStepCharge, renderPneuStepDiametre);
    }

    function renderPneuIndiceStep(n, title, champ, field, nextFn, backFn, rerender, labelDispo, labelToutes, lienLabel, guideTitle, guideBody, riskList) {
      body.innerHTML =
        renderPneuChrome(n, title, backFn) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Cette information est inscrite sur le flanc du pneu juste après la dimension (ex&nbsp;: 205/55 R16 <strong>91V</strong>) ou dans le carnet d\'entretien.</p>' +
        renderPneuGrille(champ, field, labelDispo, labelToutes, lienLabel) +
        '<button type="button" class="mauto-pneu-link" data-action="pneu-ne-sait-pas">Je ne sais pas</button>' +
        '<div class="mauto-pneu-guide">' +
          '<button type="button" class="mauto-pneu-guide-toggle" data-action="pneu-guide-toggle">Guide d\'achat <span>▾</span></button>' +
          '<div class="mauto-pneu-guide-body" hidden>' +
            '<p><strong>' + esc(guideTitle) + '</strong></p><p>' + guideBody + '</p>' +
            '<p><strong>Risque en cas d\'erreur&nbsp;:</strong></p>' +
            '<ul>' + riskList.map(function (r) { return '<li>' + esc(r) + '</li>'; }).join('') + '</ul>' +
          '</div>' +
        '</div>';
      wirePneuChrome(backFn);
      wirePneuGrille(champ, field, nextFn, rerender);
      body.querySelector('[data-action="pneu-ne-sait-pas"]').addEventListener('click', function () {
        state.pneu[field] = NE_SAIT_PAS;
        nextFn();
      });
      body.querySelector('[data-action="pneu-guide-toggle"]').addEventListener('click', function () {
        var g = body.querySelector('.mauto-pneu-guide-body');
        g.hidden = !g.hidden;
      });
    }

    // Étape 4 — Indice de charge.
    function renderPneuStepCharge() {
      renderPneuIndiceStep(4, 'Indice de charge', 'charge', 'indiceCharge',
        renderPneuStepVitesse, renderPneuStepDiametre, renderPneuStepCharge,
        'Indices de charge disponibles en ' + dimensionLisible(),
        'Tous les indices de charge', 'Voir tous les indices de charge',
        'Indice de charge',
        'Plus il est élevé, plus la charge maximale supportée augmente. Il est obligatoire d\'utiliser des pneus dont l\'indice est égal ou supérieur aux préconisations constructeur (carnet d\'entretien). Vous pouvez monter d\'un ou plusieurs indices, mais jamais en dessous de la préconisation.',
        ['Risque de déjantage', 'Risque d\'éclatement du pneumatique']);
    }

    // Étape 5 — Indice de vitesse.
    function renderPneuStepVitesse() {
      renderPneuIndiceStep(5, 'Indice de vitesse', 'vitesse', 'indiceVitesse',
        renderPneuStepSaison, renderPneuStepCharge, renderPneuStepVitesse,
        'Indices de vitesse disponibles en ' + dimensionLisible(),
        'Tous les indices de vitesse', 'Voir tous les indices de vitesse',
        'Indice de vitesse',
        'Il indique la vitesse maximale supportée par le pneu. Comme pour l\'indice de charge, il ne doit jamais être inférieur à la préconisation constructeur — vous pouvez en revanche monter en gamme.',
        ['Perte d\'adhérence', 'Risque d\'éclatement suite à une surchauffe', 'Véhicule refusé au contrôle technique']);
    }

    // Étape 6 — Saison + véhicule électrique : seule étape réellement optionnelle
    // ("Passer cette étape" toujours actif ; "Valider" nécessite d'avoir choisi une saison).
    function renderPneuStepSaison() {
      var qte = state.pneu.quantite;
      var qteIsCustom = !!qte && qte !== '2' && qte !== '4';
      var qteCustomVisible = qteIsCustom || state._qteAutreOpen;
      var saisons = saisonsProposees();
      body.innerHTML =
        renderPneuChrome(6, 'Paramètres', renderPneuStepVitesse) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Quels pneus pour quelle saison ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>' + saisons.map(function (s) { return SAISON_EXPLICATIONS[s.key]; }).filter(Boolean).join(' ') + '</p>' +
        '<p class="mauto-devis-field-label">Sélectionnez la saison de vos pneus</p>' +
        '<div class="mauto-pneu-saison-grid">' +
          saisons.map(function (s) {
            return '<button type="button" class="mauto-pneu-saison' + (state.pneu.saison === s.key ? ' is-selected' : '') + '" data-value="' + s.key + '">' +
              '<span class="mauto-pneu-saison-icon">' + icone(s.key) + '</span><span>' + esc(s.label) + '</span></button>';
          }).join('') +
        '</div>' +
        '<p class="mauto-devis-field-label">Nombre de pneus à changer</p>' +
        '<div class="mauto-pneu-grid">' +
          '<button type="button" class="mauto-pneu-option' + (qte === '2' ? ' is-selected' : '') + '" data-qte="2">2 pneus</button>' +
          '<button type="button" class="mauto-pneu-option' + (qte === '4' ? ' is-selected' : '') + '" data-qte="4">4 pneus</button>' +
          '<button type="button" class="mauto-pneu-option' + (qteCustomVisible ? ' is-selected' : '') + '" data-qte="autre">Autre</button>' +
        '</div>' +
        (qteCustomVisible ?
          '<div class="mauto-devis-field mauto-pneu-qte-custom">' +
            '<input type="number" min="1" max="8" inputmode="numeric" placeholder="Nombre de pneus" value="' + (qteIsCustom ? esc(qte) : '') + '">' +
          '</div>'
        : '') +
        '<p class="mauto-devis-field-label">Spécificité véhicule</p>' +
        '<label class="mauto-pneu-toggle-row">' +
          '<span>Véhicule électrique</span>' +
          '<input type="checkbox" class="mauto-pneu-ve-toggle"' + (state.pneu.vehiculeElectrique ? ' checked' : '') + '>' +
        '</label>' +
        '<div class="mauto-devis-actions mauto-pneu-actions-stacked">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-secondary" data-action="pneu-skip"' + (state.pneu.quantite ? '' : ' disabled') + '>Passer cette étape</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pneu-valider"' + (state.pneu.saison && state.pneu.quantite ? '' : ' disabled') + '>Valider</button>' +
        '</div>';
      wirePneuChrome(renderPneuStepVitesse);
      var validerBtn = body.querySelector('[data-action="pneu-valider"]');
      var skipBtn = body.querySelector('[data-action="pneu-skip"]');
      var saisonBtns = body.querySelectorAll('.mauto-pneu-saison');
      for (var i = 0; i < saisonBtns.length; i++) {
        saisonBtns[i].addEventListener('click', function (ev) {
          state.pneu.saison = ev.currentTarget.getAttribute('data-value');
          var all = body.querySelectorAll('.mauto-pneu-saison');
          for (var j = 0; j < all.length; j++) all[j].classList.toggle('is-selected', all[j] === ev.currentTarget);
          validerBtn.disabled = !state.pneu.quantite;
        });
      }
      var qteBtns = body.querySelectorAll('[data-qte]');
      for (var k = 0; k < qteBtns.length; k++) {
        qteBtns[k].addEventListener('click', function (ev) {
          var v = ev.currentTarget.getAttribute('data-qte');
          if (v === 'autre') {
            state._qteAutreOpen = true;
            state.pneu.quantite = '';
          } else {
            state._qteAutreOpen = false;
            state.pneu.quantite = v;
          }
          renderPneuStepSaison();
        });
      }
      var qteCustomInput = body.querySelector('.mauto-pneu-qte-custom input');
      if (qteCustomInput) {
        qteCustomInput.focus();
        qteCustomInput.addEventListener('input', function (ev) {
          state.pneu.quantite = ev.target.value.trim();
          skipBtn.disabled = !state.pneu.quantite;
          validerBtn.disabled = !(state.pneu.saison && state.pneu.quantite);
        });
      }
      body.querySelector('.mauto-pneu-ve-toggle').addEventListener('change', function (ev) {
        state.pneu.vehiculeElectrique = ev.target.checked;
      });
      skipBtn.addEventListener('click', endPneuFlow);
      validerBtn.addEventListener('click', endPneuFlow);
    }

    // Étape 1 — Prestation (liste dédiée à ce flux, distincte de celle du widget de devis).
    function renderStepPrestation() {
      setStep(1, null);
      var options = '<option value="">— Choisissez une prestation —</option>';
      RDV_PRESTATIONS.forEach(function (label) {
        options += '<option value="' + esc(label) + '">' + esc(label) + '</option>';
      });
      options += '<option value="' + esc(AUTRE_LABEL) + '">Autre (précisez)</option>';
      body.innerHTML =
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-prestation">Quelle prestation souhaitez-vous ?</label>' +
          '<select id="mauto-rdv-prestation" class="mauto-devis-select">' + options + '</select>' +
        '</div>';
      var select = body.querySelector('#mauto-rdv-prestation');
      if (state.prestation) select.value = state.prestation;
      // Sur desktop, les flèches clavier (select focus + fermé) ou la molette au survol
      // déclenchent un "change" à CHAQUE incrément, pas seulement au choix final — sans ce
      // léger débounce, l'assistant avance dès la première valeur traversée (ex. l'option
      // juste après le choix précédent) et l'utilisateur n'atteint jamais "Pneus (achat et
      // pose)" en descendant la liste. Le picker natif mobile ne déclenche qu'un seul "change"
      // final, d'où l'absence du bug sur mobile (cf. bug rapporté 2026-08-24). 180ms est
      // imperceptible pour un clic/sélection normale mais absorbe une rafale clavier/molette.
      var prestationChangeTimer = null;
      select.addEventListener('change', function () {
        clearTimeout(prestationChangeTimer);
        prestationChangeTimer = setTimeout(function () {
          if (!select.value) return;
          state.prestation = select.value;
          if (select.value === AUTRE_LABEL) renderStepAutre();
          else if (select.value === RDV_PNEU_PRESTATION) startPneuFlow();
          else goToStep(2);
        }, 180);
      });
    }

    // "Autre" : champ libre obligatoire, affiché immédiatement après ce choix à l'étape 1.
    function renderStepAutre() {
      setStep(1, renderStepPrestation);
      body.innerHTML =
        '<p class="mauto-devis-field-label">Décrivez la prestation souhaitée :</p>' +
        '<div class="mauto-devis-field">' +
          '<textarea id="mauto-rdv-autre-desc" rows="3" placeholder="Ex. bruit suspect au freinage, voyant moteur allumé…" required>' + esc(state.autreDescription) + '</textarea>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-rdv-autre-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next">Continuer</button>' +
        '</div>';
      body.querySelector('[data-action="back"]').addEventListener('click', renderStepPrestation);
      body.querySelector('[data-action="next"]').addEventListener('click', function () {
        var desc = body.querySelector('#mauto-rdv-autre-desc').value.trim();
        var errorEl = body.querySelector('.mauto-rdv-autre-error');
        if (!desc) {
          errorEl.textContent = 'Merci de décrire la prestation souhaitée.';
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        state.autreDescription = desc;
        goToStep(2);
      });
    }

    // Étape 2 — Date souhaitée : calendrier, week-ends et jours fériés grisés, min = +48h.
    function renderStepDate() {
      // Panier (state.cartPrestations) ou handoff direct pneu (state.pneuPrefilled) : pas de
      // retour possible ici — il n'existe pas de vraie étape 1 à réafficher (le choix a été fait
      // en amont, sur la fiche produit ou dans devis-widget.js). Repartir sur renderStepPrestation()
      // ferait apparaître le dropdown mono-prestation de ce widget, sans rapport avec ce choix.
      var dateBackHandler = (state.cartPrestations.length || state.pneuPrefilled)
        ? null
        : function () { (state.prestation === AUTRE_LABEL ? renderStepAutre : renderStepPrestation)(); };
      setStep(2, dateBackHandler);

      // Sans le fichier de règles partagé, aucune date ne peut être qualifiée : on le dit au
      // client plutôt que d'afficher un calendrier dont les disponibilités seraient fausses.
      if (!CAL) {
        body.innerHTML = '<p class="mauto-devis-error">Le calendrier est momentanément indisponible. ' +
          'Appelez-nous au 02 40 26 86 61, nous fixons votre rendez-vous ensemble.</p>';
        return;
      }

      var minDate = CAL.dateMinParHeures(RDV_MIN_DELAY_H);

      var viewYear, viewMonth;
      if (state.dateSouhaitee) {
        var parts = state.dateSouhaitee.split('-');
        viewYear = Number(parts[0]); viewMonth = Number(parts[1]) - 1;
      } else {
        viewYear = minDate.getFullYear(); viewMonth = minDate.getMonth();
      }
      var minViewYear = minDate.getFullYear(), minViewMonth = minDate.getMonth();

      body.innerHTML =
        '<p class="mauto-devis-field-label">Quelle date vous conviendrait ?</p>' +
        '<div class="mauto-devis-calendar">' +
          '<div class="mauto-devis-calendar-nav">' +
            '<button type="button" class="mauto-devis-calendar-arrow" data-action="prev-month" aria-label="Mois précédent">‹</button>' +
            '<span class="mauto-devis-calendar-label"></span>' +
            '<button type="button" class="mauto-devis-calendar-arrow" data-action="next-month" aria-label="Mois suivant">›</button>' +
          '</div>' +
          '<div class="mauto-devis-calendar-weekdays">' +
            WEEKDAY_LABELS.map(function (w) { return '<span>' + w + '</span>'; }).join('') +
          '</div>' +
          '<div class="mauto-devis-calendar-days"></div>' +
        '</div>' +
        '<p class="mauto-devis-calendar-urgence">Besoin d’une intervention urgente&nbsp;? Contactez-nous directement au <a href="tel:0240268661">02 40 26 86 61</a>.</p>' +
        '<p class="mauto-devis-error mauto-rdv-date-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          (dateBackHandler ? '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' : '') +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next"' + (state.dateSouhaitee ? '' : ' disabled') + '>Continuer</button>' +
        '</div>';

      var labelEl = body.querySelector('.mauto-devis-calendar-label');
      var daysEl = body.querySelector('.mauto-devis-calendar-days');
      var nextBtn = body.querySelector('[data-action="next"]');
      var prevMonthBtn = body.querySelector('[data-action="prev-month"]');
      var nextMonthBtn = body.querySelector('[data-action="next-month"]');

      function renderMonth() {
        labelEl.textContent = MONTH_NAMES[viewMonth] + ' ' + viewYear;
        prevMonthBtn.disabled = (viewYear === minViewYear && viewMonth === minViewMonth);

        var firstOfMonth = new Date(viewYear, viewMonth, 1);
        var firstWeekday = (firstOfMonth.getDay() + 6) % 7; // 0 = lundi (grille FR)
        var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

        var html = '';
        for (var i = 0; i < firstWeekday; i++) html += '<span class="mauto-devis-calendar-day is-empty"></span>';
        for (var day = 1; day <= daysInMonth; day++) {
          var d = new Date(viewYear, viewMonth, day);
          var dateStr = toDateStr(d);
          var selectable = isSelectableDate(d, minDate, fermetures);
          var isSelected = state.dateSouhaitee === dateStr;
          html += '<button type="button" class="mauto-devis-calendar-day' +
            (selectable ? '' : ' is-disabled') + (isSelected ? ' is-selected' : '') + '"' +
            (selectable ? ' data-date="' + dateStr + '"' : ' disabled') + '>' + day + '</button>';
        }
        daysEl.innerHTML = html;

        var dayBtns = daysEl.querySelectorAll('.mauto-devis-calendar-day[data-date]');
        for (var k = 0; k < dayBtns.length; k++) {
          dayBtns[k].addEventListener('click', function (ev) {
            state.dateSouhaitee = ev.currentTarget.getAttribute('data-date');
            body.querySelector('.mauto-rdv-date-error').hidden = true;
            nextBtn.disabled = false;
            renderMonth();
          });
        }
      }
      renderMonth();

      prevMonthBtn.addEventListener('click', function () {
        viewMonth--; if (viewMonth < 0) { viewMonth = 11; viewYear--; }
        renderMonth();
      });
      nextMonthBtn.addEventListener('click', function () {
        viewMonth++; if (viewMonth > 11) { viewMonth = 0; viewYear++; }
        renderMonth();
      });

      var backBtn = body.querySelector('[data-action="back"]');
      if (backBtn) backBtn.addEventListener('click', dateBackHandler);
      nextBtn.addEventListener('click', function () {
        if (!state.dateSouhaitee) {
          body.querySelector('.mauto-rdv-date-error').textContent = 'Merci de choisir une date.';
          body.querySelector('.mauto-rdv-date-error').hidden = false;
          return;
        }
        goToStep(3);
      });
    }

    // Étape 3 — Créneau horaire de dépôt du véhicule, obligatoire.
    function renderStepCreneau() {
      var backHandler = function () { goToStep(2); };
      setStep(3, backHandler);
      body.innerHTML =
        '<p class="mauto-devis-field-label">À quel créneau souhaitez-vous déposer votre véhicule ?</p>' +
        '<div class="mauto-devis-creneaux">' +
          CRENEAUX.map(function (c) {
            return '<button type="button" class="mauto-devis-creneau' +
              (state.creneauSouhaite === c ? ' is-selected' : '') + '" data-creneau="' + esc(c) + '">' + esc(c) + '</button>';
          }).join('') +
        '</div>' +
        '<p class="mauto-devis-calendar-urgence">Besoin d\'un véhicule de prêt&nbsp;? Contactez-nous directement au <a href="tel:0240268661">02 40 26 86 61</a>.</p>' +
        '<p class="mauto-devis-error mauto-rdv-creneau-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next"' + (state.creneauSouhaite ? '' : ' disabled') + '>Continuer</button>' +
        '</div>';

      var nextBtn = body.querySelector('[data-action="next"]');
      var creneauBtns = body.querySelectorAll('.mauto-devis-creneau');
      for (var i = 0; i < creneauBtns.length; i++) {
        creneauBtns[i].addEventListener('click', function (ev) {
          state.creneauSouhaite = ev.currentTarget.getAttribute('data-creneau');
          body.querySelector('.mauto-rdv-creneau-error').hidden = true;
          nextBtn.disabled = false;
          var all = body.querySelectorAll('.mauto-devis-creneau');
          for (var j = 0; j < all.length; j++) {
            all[j].classList.toggle('is-selected', all[j] === ev.currentTarget);
          }
        });
      }

      body.querySelector('[data-action="back"]').addEventListener('click', backHandler);
      nextBtn.addEventListener('click', function () {
        if (!state.creneauSouhaite) {
          body.querySelector('.mauto-rdv-creneau-error').textContent = 'Merci de choisir un créneau.';
          body.querySelector('.mauto-rdv-creneau-error').hidden = false;
          return;
        }
        goToStep(4);
      });
    }

    // Étape 4 — Coordonnées client.
    function renderStepCoordonnees() {
      function captureCoordDraft() {
        state.client = {
          nom: body.querySelector('#mauto-rdv-nom').value,
          prenom: body.querySelector('#mauto-rdv-prenom').value,
          telephone: body.querySelector('#mauto-rdv-telephone').value,
          telephoneFixe: body.querySelector('#mauto-rdv-telephone-fixe').value,
          email: body.querySelector('#mauto-rdv-email').value,
          codePostal: body.querySelector('#mauto-rdv-codepostal').value,
        };
      }
      var backHandler = function () { captureCoordDraft(); goToStep(3); };
      setStep(4, backHandler);
      body.innerHTML =
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-nom">Nom *</label>' +
          '<input type="text" id="mauto-rdv-nom" value="' + esc(state.client.nom) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-prenom">Prénom *</label>' +
          '<input type="text" id="mauto-rdv-prenom" value="' + esc(state.client.prenom) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-telephone">Téléphone portable *</label>' +
          '<input type="tel" id="mauto-rdv-telephone" value="' + esc(state.client.telephone) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-telephone-fixe">Téléphone fixe (optionnel)</label>' +
          '<input type="tel" id="mauto-rdv-telephone-fixe" value="' + esc(state.client.telephoneFixe) + '">' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-email">Email *</label>' +
          '<input type="email" id="mauto-rdv-email" value="' + esc(state.client.email) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-rdv-codepostal">Code postal *</label>' +
          '<input type="text" id="mauto-rdv-codepostal" value="' + esc(state.client.codePostal) + '" inputmode="numeric" required>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-rdv-coord-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next">Continuer</button>' +
        '</div>';
      body.querySelector('[data-action="back"]').addEventListener('click', backHandler);
      body.querySelector('[data-action="next"]').addEventListener('click', function () {
        var nom = body.querySelector('#mauto-rdv-nom').value.trim();
        var prenom = body.querySelector('#mauto-rdv-prenom').value.trim();
        var telephone = body.querySelector('#mauto-rdv-telephone').value.trim();
        var telephoneFixe = body.querySelector('#mauto-rdv-telephone-fixe').value.trim();
        var email = body.querySelector('#mauto-rdv-email').value.trim();
        var codePostal = body.querySelector('#mauto-rdv-codepostal').value.trim();
        var errorEl = body.querySelector('.mauto-rdv-coord-error');
        if (!nom || !prenom || !telephone || !email || !codePostal) {
          errorEl.textContent = 'Merci de renseigner nom, prénom, téléphone, email et code postal.';
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        state.client = { nom: nom, prenom: prenom, telephone: telephone, telephoneFixe: telephoneFixe, email: email, codePostal: codePostal };
        goToStep(5);
      });
    }

    // Étape 5 — Véhicule + soumission finale.
    function renderStepVehicule() {
      function captureVehiculeDraft() {
        state.vehicule = {
          immatriculation: body.querySelector('#mauto-rdv-immatriculation').value,
          marque: body.querySelector('#mauto-rdv-marque').value,
          modele: body.querySelector('#mauto-rdv-modele').value,
        };
      }
      var backHandler = function () { captureVehiculeDraft(); goToStep(4); };
      setStep(5, backHandler);
      body.innerHTML =
        '<form class="mauto-devis-form">' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-rdv-immatriculation">Immatriculation *</label>' +
            '<input type="text" id="mauto-rdv-immatriculation" placeholder="Ex. AB-123-CD" value="' + esc(state.vehicule.immatriculation) + '" required>' +
          '</div>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-rdv-marque">Marque *</label>' +
            '<input type="text" id="mauto-rdv-marque" placeholder="Ex. Peugeot" value="' + esc(state.vehicule.marque) + '" required>' +
          '</div>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-rdv-modele">Modèle *</label>' +
            '<input type="text" id="mauto-rdv-modele" placeholder="Ex. 208" value="' + esc(state.vehicule.modele) + '" required>' +
          '</div>' +
          '<p class="mauto-devis-error mauto-rdv-vehicule-error" hidden></p>' +
          '<div class="mauto-devis-actions">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
            '<button type="submit" class="mauto-devis-btn mauto-devis-btn-primary">Envoyer ma demande de rendez-vous</button>' +
          '</div>' +
        '</form>';
      body.querySelector('[data-action="back"]').addEventListener('click', backHandler);
      body.querySelector('form').addEventListener('submit', onSubmit);
    }

    function renderDone() {
      body.innerHTML =
        '<div class="mauto-devis-done">' +
          '<p class="mauto-devis-done-title">' + icone('valide') + ' Votre demande a bien été envoyée</p>' +
          '<p>Un email de confirmation vient de vous être adressé. Nous revenons vers vous rapidement pour confirmer votre rendez-vous.</p>' +
        '</div>';
    }

    function onSubmit(ev) {
      ev.preventDefault();
      var form = ev.target;
      var errorEl = form.querySelector('.mauto-rdv-vehicule-error');
      errorEl.hidden = true;

      var immatRaw = form.querySelector('#mauto-rdv-immatriculation').value.trim();
      var marque = form.querySelector('#mauto-rdv-marque').value.trim();
      var modele = form.querySelector('#mauto-rdv-modele').value.trim();

      if (!immatRaw || !marque || !modele) {
        errorEl.textContent = "Merci de renseigner l'immatriculation, la marque et le modèle.";
        errorEl.hidden = false;
        return;
      }
      if (!IMMAT_REGEX.test(immatRaw)) {
        errorEl.textContent = "Format d'immatriculation invalide — utilisez le format AB-123-CD.";
        errorEl.hidden = false;
        return;
      }
      state.vehicule = { immatriculation: normalizeImmat(immatRaw), marque: marque, modele: modele };

      var submitBtn = form.querySelector('button[type="submit"]');
      var originalLabel = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Envoi…';

      // Panier (state.cartPrestations, ≥2 prestations) : payload et endpoint dédiés
      // (/api/rdv-groupe). Mono-prestation (dropdown direct, ou handoff 1 item) : payload et
      // endpoint /api/rdv strictement inchangés, cf. en-tête de ce fichier.
      var isGroupe = state.cartPrestations.length > 0;
      var payload, endpoint;
      if (isGroupe) {
        payload = {
          prestations: state.cartPrestations,
          dateSouhaitee: state.dateSouhaitee,
          creneauSouhaite: state.creneauSouhaite,
          nom: state.client.nom,
          prenom: state.client.prenom,
          telephone: state.client.telephone,
          telephoneFixe: state.client.telephoneFixe,
          email: state.client.email,
          codePostal: state.client.codePostal,
          vehicule: state.vehicule,
          pneu: state.pneu,
          source: 'bouton-flottant-rdv',
        };
        endpoint = '/api/rdv-groupe';
      } else {
        payload = {
          prestation: state.prestation,
          autreDescription: state.prestation === AUTRE_LABEL ? state.autreDescription : '',
          dateSouhaitee: state.dateSouhaitee,
          creneauSouhaite: state.creneauSouhaite,
          nom: state.client.nom,
          prenom: state.client.prenom,
          telephone: state.client.telephone,
          telephoneFixe: state.client.telephoneFixe,
          email: state.client.email,
          codePostal: state.client.codePostal,
          vehicule: state.vehicule,
          pneu: state.pneu,
          source: 'bouton-flottant-rdv',
        };
        endpoint = '/api/rdv';
      }

      fetch(apiBase + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok || !res.body.ok) throw new Error((res.body && res.body.error) || 'Erreur serveur');
          renderDone();
        })
        .catch(function (err) {
          errorEl.textContent = err.message || "Une erreur est survenue lors de l'envoi. Merci de réessayer ou de nous appeler directement.";
          errorEl.hidden = false;
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
        });
    }

    // Prestation(s) déjà connue(s) (arrivée via handoffToRdv, cf. en-tête de ce fichier) : l'étape
    // 1 est sautée, direction l'étape 2 (Date). Panier (state.cartPrestations) : si "Pneus" en
    // fait partie, le tiroir dimension se déclenche une fois pour cet item comme pour un choix
    // unique, puis enchaîne sur Date exactement pareil (cf. endPneuFlow → goToStep(2)). Retour
    // ultérieur : dateBackHandler ramène à renderStepPrestation() comme pour un parcours
    // classique, avec le dropdown pré-sélectionné (cas mono-prestation uniquement).
    // Les vignettes sont peintes une seule fois, avant le premier écran : leur contenu ne dépend
    // d'aucune donnée chargée à distance, contrairement au widget de devis qui attend les tarifs.
    renderQuickPicksBar();

    if (state.cartPrestations.length) {
      if (state.cartPrestations.indexOf(RDV_PNEU_PRESTATION) !== -1) startPneuFlow();
      else renderStepDate();
    }
    else if (state.pneuPrefilled) renderStepDate();
    else if (state.prestation === RDV_PNEU_PRESTATION) startPneuFlow();
    else if (state.prestation) renderStepDate();
    else renderStepPrestation();
  }

  // Exposée pour devis-floating-widget.js (montage à la demande dans son overlay dédié).
  window.MautoRdvWidget = { initWidget: initWidget };
})();
